import { useRef, RefObject } from "react"
import { FormikProps } from "formik"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { createServiceCenterMessage, ServiceCenterMessagesQuery } from "../serviceCenterMessagesApi"
import { selectUserId } from "../../../../login/loginSelectors"
import { selectEffectiveServiceCenterId } from "../../../../context/contextSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import { UserType } from "../../../../login/loginTypes"
import {
    CreateEditMessageFormValues,
    MessageAdditionalDataRecipientsType,
    MessageItem,
    ServiceCenterMessageAdditionalData,
} from "../serviceCenterMessagesTypes"

type UseCreateServiceCenterMessageFlowResult = {
    formRef: RefObject<FormikProps<CreateEditMessageFormValues>>
    handleSaveClick: () => void
    handleSubmit: (data: MessageItem) => void
}

export function useCreateServiceCenterMessageFlow(
    onAfterSuccess?: () => void,
): UseCreateServiceCenterMessageFlowResult {
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const userId = useAppSelector(selectUserId)

    const formRef = useRef<FormikProps<CreateEditMessageFormValues>>(null)

    const messageAdditionalData: ServiceCenterMessageAdditionalData = {
        sender: {
            type: UserType.SERVICE_CENTER,
            serviceCenter: {
                id: serviceCenterId || 0,
            },
            user: {
                id: userId || 0,
            },
        },
        recipients: [
            {
                type: MessageAdditionalDataRecipientsType.SERVICE_CENTER_USERS,
                userTypes: [UserType.USER],
            },
        ],
    }

    const { mutateAsync: callCreateMessage } = useApiMutation(createServiceCenterMessage, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ServiceCenterMessagesQuery.LIST] })
            onAfterSuccess?.()
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const handleSubmit = (data: MessageItem) => {
        void callCreateMessage({
            message: data,
            messageAdditionalData,
        })
    }

    return {
        formRef,
        handleSaveClick,
        handleSubmit,
    }
}
